import { defineStore } from 'pinia'
import { useGtm } from '@gtm-support/vue-gtm'
import { getDataLayer, checkOneTrustLoadState, mapDataLayerObj } from '#root/utils/helpers/helperTracking.js'
import { useSetter } from '#root/utils/helpers/helperRef.js'
import { useGlobalStore } from '#root/stores/storeGlobal.js'
import { usePagesStore } from '#root/stores/storePages.js'
import { e164NumCodes } from '#root/utils/types/tracking.js'

export const useTrackingStore = defineStore('tracking', () => {
  const gtm = useGtm()
  const globalStore = useGlobalStore()
  const [lastActiveField, setLastActiveField] = useSetter(null)
  const [interactionIntentTracked, setInteractionIntentTracked] = useSetter(false)
  const [abandonTracked, setAbandonTracked] = useSetter(false)
  const [ongoingLeadType, setOngoingLeadType] = useSetter(null)
  const [webRefCode, setWebRefCode] = useSetter(null)
  const [clickRef, setClickRef] = useSetter(null)
  const [gtmActive, setGtmActive] = useSetter(false)
  const [onetrustAccepted, setOnetrustAccepted] = useSetter(false)
  const [inContentCtaCountersElms, setInContentCtaCountersElms] = useSetter({
    quote: [],
    learnMore: []
  })

  // store actions
  const clearOngoingLeadType = () => {
    ongoingLeadType.value = null
  }
  const mapDataLayerOneTrustLoadOrder = (pageContext) => {
    if (globalStore.isBot || !globalStore.trackingEnabled) {
      return
    }
    getDataLayer().unshift(mapDataLayerObj(pageContext))
    checkOneTrustLoadState(pageContext)
  }
  const trackFormFieldInteraction = (event) => {
    if (globalStore.isBot || !globalStore.trackingEnabled) {
      return
    }

    const field = event.currentTarget
    const wasValid = field.value !== ''
    const name = field.getAttribute('name')
    const label = name === 'provinceId' || name === 'location' ? (field._value?.text ?? name) : name

    if (wasValid === true) {
      if (name === 'provinceId' || name === 'location') setInteractionIntentTracked(true)

      gtm.trackEvent({
        interactionCategory: 'FormTracking',
        interactionAction: 'CompleteField',
        interactionLabel: label,
        event: 'nfs.formTracking'
      })

      if (interactionIntentTracked.value === false) {
        gtm.trackEvent({
          interactionCategory: 'FormTracking',
          interactionAction: 'InteractionIntent',
          interactionLabel: label,
          event: 'nfs.formTracking'
        })
        setInteractionIntentTracked(true)
      }
    } else {
      gtm.trackEvent({
        interactionCategory: 'FormTracking',
        interactionAction: 'SkipField',
        interactionLabel: label,
        event: 'nfs.formTracking'
      })
    }
  }
  const trackRouteChange = (pageContext) => {
    if (!globalStore.trackingEnabled) return
    const pageStore = usePagesStore()
    gtm.trackEvent({
      event: 'content-view',
      'content-name': pageContext.urlPathname,
      'content-view-name': pageStore.template
    })
  }
  const trackLead = ({ leadType, assignedBrand, formType }) => {
    const { queryParams, transactionId } = useGlobalStore()

    if (globalStore.isBot || !globalStore.trackingEnabled) {
      return
    }

    const ppclid = queryParams?.ppclid || ''
    const ppcrid = queryParams?.ppcrid || ''
    const ppsubid = queryParams?.ppsubid || ''
    const leadId = transactionId
    const pushData = {
      ppclid,
      ppcrid,
      ppsubid,
      leadType,
      leadId,
      ...(formType ? { formType: formType } : { formType: leadType }),
      ...(assignedBrand ? { 'thank-you-page': assignedBrand } : ''),
      event: 'nfs.leadCreated'
    }

    gtm.trackEvent(pushData)
    // commit('clearOngoingLeadType')
    clearOngoingLeadType()
  }
  const trackMultiStepProgress = ({ step, index, province = false }) => {
    if (globalStore.isBot || !globalStore.trackingEnabled || !step) {
      return
    }

    gtm.trackEvent({
      interactionCategory: 'form progression',
      interactionAction: `step ${index}`,
      interactionLabel: step,
      'interaction-type': false,
      event: 'nfs.formProgression',
      ...(step.includes('location') && province ? { province: province } : {})
    })
  }
  const trackABTesting = ({ testName, testVariation }) => {
    if (globalStore.isBot || !globalStore.trackingEnabled) {
      return
    }

    gtm.trackEvent({
      abTestName: testName,
      abTestVariation: testVariation,
      event: 'nfs.abTestingUpdate'
    })
  }
  const trackUserInfo = ({ firstName, lastName, phoneH, phoneM, dob, email }) => {
    if (globalStore.isBot || !globalStore.trackingEnabled) {
      return
    }

    const phoneToSend = phoneH || phoneM
    const phoneToUse = phoneToSend
      ? `${e164NumCodes[globalStore.locale]}${phoneToSend.charAt(0) === '0' ? phoneToSend.substring(1) : phoneToSend}`
      : ''
    const emailToSend = email ? email.toLowerCase() : ''
    if (firstName && lastName) {
      const d = dob ? new Date(dob) : ''
      const day = d && d.getUTCDate() < 10 ? `0${d.getUTCDate()}` : d && d.getUTCDate()
      const month = d && d.getMonth() + 1 < 10 ? `0${d.getMonth() + 1}` : d && d.getMonth() + 1
      const userInfo = {
        send: true,
        fn: firstName.toLowerCase(),
        ln: lastName.toLowerCase(),
        ph: phoneToUse,
        dob: d ? `${d.getFullYear()}-${month}-${day}` : '',
        em: emailToSend,
        createdBy: 1
      }

      gtm.trackEvent({
        // Missing event property, using 'nfs.userInfo' temporary
        userInfo,
        event: 'nfs.userInfo'
      })
    }
  }
  const trackCallback = ({ label }) => {
    if (globalStore.isBot || !globalStore.trackingEnabled) {
      return
    }

    gtm.trackEvent({
      interactionCategory: 'AppointmentTracking',
      interactionAction: 'Clicked',
      interactionLabel: label,
      event: 'nfs.appointmentTracking'
    })
  }
  const trackFormError = (formFieldName, errorLog, quoteFormType) => {
    if (globalStore.isBot || !globalStore.trackingEnabled) {
      return
    }

    gtm.trackEvent({
      formFieldName,
      errorLog,
      quoteFormType,
      event: 'nfs.formErrorLog'
    })
  }
  const trackCoverCalculator = (amount) => {
    if (globalStore.isBot || !globalStore.trackingEnabled) {
      return
    }

    gtm.trackEvent({
      event: 'nts.calculateAmount',
      value: amount,
      interactionCategory: 'BenefitAmountTracking',
      interactionLabel: 'benefitAmount',
      interactionAction: 'Clicked',
      'interaction-type': false
    })
  }
  return {
    lastActiveField,
    interactionIntentTracked,
    abandonTracked,
    ongoingLeadType,
    webRefCode,
    clickRef,
    gtmActive,
    setLastActiveField,
    setInteractionIntentTracked,
    setAbandonTracked,
    setOngoingLeadType,
    clearOngoingLeadType,
    setWebRefCode,
    setClickRef,
    mapDataLayerOneTrustLoadOrder,
    setGtmActive,
    trackFormFieldInteraction,
    trackLead,
    trackABTesting,
    trackUserInfo,
    trackCallback,
    trackMultiStepProgress,
    trackFormError,
    trackRouteChange,
    onetrustAccepted,
    setOnetrustAccepted,
    inContentCtaCountersElms,
    setInContentCtaCountersElms,
    trackCoverCalculator
  }
})
